import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { ProfileFieldInput } from "../../components/ProfileFieldInput";
import { api, errorMessage } from "../../lib/api";
import { useAuth, can } from "../../lib/auth";
import { GROUP_LABELS, groupFields } from "../../lib/fieldMeta";
import { useLang } from "../../lib/i18n";

const CORE = { name_en: "", name_ar: "", title_en: "", title_ar: "", email: "", role: "employee" };

const CoreField = ({ id, label, value, onChange, dir = "ltr", required, readOnly }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}{required && <span className="text-wade"> *</span>}</Label>
    <Input id={id} dir={dir} value={value} required={required} disabled={readOnly} onChange={(e) => onChange(e.target.value)}
      className={`h-12 rounded-xl ${dir === "rtl" ? "font-arabic text-right" : ""}`} data-testid={`employee-form-${id}`} />
  </div>
);

export const EmployeeDialog = ({ open, onOpenChange, employee, onSaved, meta }) => {
  const { user } = useAuth();
  const { t, lang, pick } = useLang();
  const [core, setCore] = useState(CORE);
  const [profile, setProfile] = useState({});
  const [theme, setTheme] = useState("inception-black");
  const [saving, setSaving] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const canEdit = !employee || can(user, "employees.edit");
  const canRole = can(user, "employees.assign_role") && employee?.email !== user.email
    && (user.is_owner || employee?.role !== "admin");
  const roles = meta.roles.filter((r) => r.key !== "admin" || user.is_owner || core.role === "admin");
  const enabled = meta.fields.filter((f) => f.enabled);

  useEffect(() => {
    if (!open) return;
    setCore(employee ? Object.fromEntries(Object.keys(CORE).map((k) => [k, employee[k] ?? ""])) : CORE);
    setProfile(employee?.profile || {});
    setTheme("inception-black");
  }, [open, employee]);
  const set = (k) => (v) => setCore((f) => ({ ...f, [k]: v }));

  const applyTemplate = (id) => {
    const tpl = meta.templates.find((t) => t.id === id);
    if (!tpl) return;
    setCore((c) => ({ ...c, role: tpl.role, title_en: tpl.title_en || c.title_en, title_ar: tpl.title_ar || c.title_ar }));
    setProfile({ ...tpl.profile });
    setTheme(tpl.theme);
    toast.success(t("Template \"{name}\" applied", { name: tpl.name }));
  };

  const resetWallet = async () => {
    try {
      await api.post(`/admin/employees/${employee.id}/wallet-reset`);
      toast.success(t("Wallet download allowed again"));
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = canEdit ? { ...core, profile: Object.fromEntries(enabled.map((f) => [f.key, profile[f.key] || ""])) } : { role: core.role };
    if (!canRole) delete payload.role;
    if (!employee) { payload.theme = theme; payload.send_invite = sendInvite; }
    try {
      if (employee) await api.patch(`/admin/employees/${employee.id}`, payload);
      else {
        const { data } = await api.post("/admin/employees", payload);
        if (data.invite_error) toast.error(t(data.invite_error));
        else if (sendInvite) toast.success(t("Invite email sent to {email}", { email: data.email }));
      }
      toast.success(t(employee ? "Employee updated" : "Employee added"));
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="employee-form-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-xl">{t(employee ? "Edit employee" : "Add employee")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          {!employee && meta.templates.length > 0 && (
            <div className="space-y-1.5 rounded-2xl bg-slate-50 p-4" data-testid="employee-form-template">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Start from template")}</Label>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="employee-form-template-select"><SelectValue placeholder={t("Choose a template")} /></SelectTrigger>
                <SelectContent>
                  {meta.templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`employee-form-template-${t.id}`}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <CoreField id="name_en" label={t("Name (English)")} value={core.name_en} onChange={set("name_en")} required readOnly={!canEdit} />
            <CoreField id="name_ar" label={t("Name (Arabic)")} value={core.name_ar} onChange={set("name_ar")} dir="rtl" readOnly={!canEdit} />
            <CoreField id="title_en" label={t("Title (English)")} value={core.title_en} onChange={set("title_en")} required readOnly={!canEdit} />
            <CoreField id="title_ar" label={t("Title (Arabic)")} value={core.title_ar} onChange={set("title_ar")} dir="rtl" readOnly={!canEdit} />
            <CoreField id="email" label={t("Email (@{domain})", { domain: meta.domain })} value={core.email} onChange={set("email")} required readOnly={!canEdit} />
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Role")}</Label>
              <Select value={core.role} onValueChange={set("role")} disabled={!canRole}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="employee-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`employee-form-role-${r.key}`}>{pick(r.name_en, r.name_ar)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {canEdit && groupFields(enabled).map(([group, list]) => (
            <fieldset key={group} className="border-t border-slate-100 pt-5">
              <legend className="mb-3 font-display text-sm font-bold text-slate-900">{GROUP_LABELS[group][lang]}</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {list.map((f) => (
                  <ProfileFieldInput key={f.key} field={{ ...f, required: f.required && f.editable_by === "admin" }} value={profile[f.key]} lang={lang}
                    testPrefix="employee-form" onChange={(k, v) => setProfile((s) => ({ ...s, [k]: v }))} />
                ))}
              </div>
            </fieldset>
          ))}
          {employee?.wallet_issued_at && can(user, "employees.edit") && (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-slate-50 p-4" data-testid="employee-wallet-status">
              <p className="me-auto text-sm text-slate-600">{t("Wallet pass downloaded on {date}", { date: new Date(employee.wallet_issued_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB") })}</p>
              <button type="button" onClick={resetWallet} data-testid="employee-wallet-reset"
                className="btn-press min-h-[42px] rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold hover:border-slate-900">
                {t("Allow new download")}
              </button>
            </div>
          )}
          {!employee && (
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-slate-50 p-4" data-testid="employee-form-invite">
              <Checkbox checked={sendInvite} onCheckedChange={(v) => setSendInvite(!!v)} data-testid="employee-form-invite-checkbox" className="mt-0.5" />
              <span>
                <span className="block text-sm font-semibold text-slate-900">{t("Send invite email")}</span>
                <span className="block text-xs text-slate-500">{t("They get an email with a sign-in link to complete their profile.")}</span>
              </span>
            </label>
          )}
          <button type="submit" disabled={saving} data-testid="employee-form-submit"
            className="btn-press min-h-[50px] w-full rounded-2xl bg-cobalt px-6 font-semibold text-white hover:bg-cobalt-hover disabled:opacity-60">
            {saving ? t("Saving…") : t(employee ? "Save changes" : "Add employee")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
